/**
 * "Save as item pool" — the S2 set becomes a record the rest of the platform
 * can address (planning Input Scope, another session, a track).
 *
 * The dialog states what will and will not be saved BEFORE the save, because
 * session items that were typed rather than pulled from the platform cannot go
 * into a pool (see saveItemPool.ts). Naming those items up front is the whole
 * point — a pool that quietly holds fewer items than the room agreed on is a
 * worse outcome than one that says what it left behind.
 *
 * Built on ds/Modal rather than a hand-rolled overlay: focus handling, escape,
 * and the accessible description come with it, and the ui-ratchet exists to
 * stop this file being the 319th place that logic was retyped.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Modal, TextInput, Field, Callout } from '../../../components/ds';
import { useSessionEngineStore } from '../store';
import {
  defaultPoolName,
  planItemPoolSave,
  saveSessionItemsAsPool,
  type SaveItemPoolResult,
} from '../saveItemPool';

const BTN =
  'inline-flex items-center gap-2 border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)]';
const BTN_PRIMARY =
  'inline-flex items-center gap-2 bg-[var(--slate)] px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-40';

export function SaveItemPoolModal({ onClose }: { onClose: () => void }) {
  const event = useSessionEngineStore((s) => s.event)!;
  const [name, setName] = useState(() => defaultPoolName(event));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<SaveItemPoolResult | null>(null);

  const plan = useMemo(() => planItemPoolSave(event.items), [event.items]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      setSaved(await saveSessionItemsAsPool(event, name));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the item pool.');
    } finally {
      setBusy(false);
    }
  }

  if (saved) {
    return (
      <Modal
        open
        onClose={onClose}
        title="Item pool saved"
        description={`It is now selectable as an Input Scope in the Planning Engine, and by other sessions.`}
        footer={
          <>
            <Link to={`/prioritization/lists/${saved.poolId}`} className={BTN}>
              Open the pool
            </Link>
            <button type="button" onClick={onClose} className={BTN}>
              Done
            </button>
          </>
        }
      >
        <p className="text-sm text-[var(--text-sec)]">
          Saved {saved.savedCount} item{saved.savedCount === 1 ? '' : 's'} as{' '}
          <strong className="text-[var(--text-pri)]">{name.trim() || defaultPoolName(event)}</strong>.
          {saved.excludedCount > 0
            ? ` ${saved.excludedCount} session-authored item${saved.excludedCount === 1 ? ' was' : 's were'} left out.`
            : ''}
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Save as item pool"
      description={`${plan.poolable.length} of ${event.items.length} item${
        event.items.length === 1 ? '' : 's'
      } will be saved, in this session's order.`}
      footer={
        <>
          <button type="button" onClick={onClose} className={BTN}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy || plan.poolable.length === 0}
            className={BTN_PRIMARY}
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save pool
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Name" htmlFor="pool-name">
          <TextInput id="pool-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>

        {plan.excluded.length > 0 && (
          <div className="border border-[var(--rule)] bg-[var(--surface)] px-3 py-2">
            <p className="font-mono text-[0.72rem] uppercase tracking-wider text-[var(--text-ter)]">
              Not saved — not platform records
            </p>
            <p className="mt-1 text-xs text-[var(--text-sec)]">
              {plan.excluded.map((i) => i.title).join(' · ')}
            </p>
            <p className="mt-1 text-xs text-[var(--text-ter)]">
              A pool addresses items by id. These were typed or imported into this session only —
              promote them to items first if the pool should carry them.
            </p>
          </div>
        )}

        {error && <Callout tone="coral" title="Could not save">{error}</Callout>}
      </div>
    </Modal>
  );
}

export default SaveItemPoolModal;
